import React, { useEffect, useState } from 'react';
import { Navbar } from '../components/Navbar';
import { Plus, Edit2, Trash2, Calendar, Percent, DollarSign } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AdminCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    type: 'percentage',
    value: '',
    valid_from: '',
    valid_until: '',
    max_uses: '',
    user_specific: false,
    seller_id: ''
  });

  const fetchCoupons = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/coupons`, { withCredentials: true });
      setCoupons(response.data);
    } catch (error) {
      toast.error('Erro ao carregar cupons');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      value: parseFloat(formData.value),
      max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
      seller_id: formData.seller_id || null
    };
    
    try {
      if (editingCoupon) {
        await axios.put(
          `${API_URL}/admin/coupons/${editingCoupon.coupon_id}`,
          payload,
          { withCredentials: true }
        );
        toast.success('Cupom atualizado!');
      } else {
        await axios.post(
          `${API_URL}/admin/coupons`,
          payload,
          { withCredentials: true }
        );
        toast.success('Cupom criado!');
      }
      
      setIsDialogOpen(false);
      setEditingCoupon(null);
      setFormData({
        code: '',
        type: 'percentage',
        value: '',
        valid_from: '',
        valid_until: '',
        max_uses: '',
        user_specific: false,
        seller_id: ''
      });
      fetchCoupons();
    } catch (error) {
      toast.error('Erro ao salvar cupom');
    }
  };

  const handleEdit = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      type: coupon.type,
      value: coupon.value.toString(),
      valid_from: new Date(coupon.valid_from).toISOString().slice(0, 16),
      valid_until: new Date(coupon.valid_until).toISOString().slice(0, 16),
      max_uses: coupon.max_uses?.toString() || '',
      user_specific: coupon.user_specific,
      seller_id: coupon.seller_id || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (couponId) => {
    if (!window.confirm('Tem certeza que deseja excluir este cupom?')) return;
    
    try {
      await axios.delete(`${API_URL}/admin/coupons/${couponId}`, { withCredentials: true });
      toast.success('Cupom excluído!');
      fetchCoupons();
    } catch (error) {
      toast.error('Erro ao excluir cupom');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="admin-coupons-title">
              Gerenciar Cupons
            </h1>
            <p className="text-lg text-gray-600">Crie e gerencie cupons de desconto</p>
          </div>
          <Button
            onClick={() => {
              setEditingCoupon(null);
              const now = new Date();
              const tomorrow = new Date(now);
              tomorrow.setDate(tomorrow.getDate() + 1);
              
              setFormData({
                code: '',
                type: 'percentage',
                value: '',
                valid_from: now.toISOString().slice(0, 16),
                valid_until: tomorrow.toISOString().slice(0, 16),
                max_uses: '',
                user_specific: false,
                seller_id: ''
              });
              setIsDialogOpen(true);
            }}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
            data-testid="add-coupon-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Cupom
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : coupons.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <p className="text-lg text-gray-600">Nenhum cupom cadastrado</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="divide-y divide-gray-200">
              {coupons.map((coupon) => (
                <div
                  key={coupon.coupon_id}
                  className="p-6 hover:bg-gray-50 transition-colors"
                  data-testid={`coupon-item-${coupon.coupon_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-xl font-bold text-gray-900 font-mono bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                          {coupon.code}
                        </h3>
                        {coupon.type === 'percentage' ? (
                          <span className="inline-flex items-center px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                            <Percent className="w-3 h-3 mr-1" />
                            {coupon.value}% OFF
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                            <DollarSign className="w-3 h-3 mr-1" />
                            R$ {coupon.value.toFixed(2)} OFF
                          </span>
                        )}
                        {!coupon.active && (
                          <span className="px-3 py-1 bg-gray-200 text-gray-700 rounded-full text-sm font-medium">
                            Inativo
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center space-x-6 text-sm text-gray-600">
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(coupon.valid_from)} - {formatDate(coupon.valid_until)}</span>
                        </div>
                        {coupon.max_uses && (
                          <span>
                            Usos: {coupon.uses_count}/{coupon.max_uses}
                          </span>
                        )}
                        {coupon.user_specific && (
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                            Específico do usuário
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(coupon)}
                        data-testid={`edit-coupon-${coupon.coupon_id}`}
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(coupon.coupon_id)}
                        data-testid={`delete-coupon-${coupon.coupon_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle data-testid="coupon-dialog-title">
              {editingCoupon ? 'Editar Cupom' : 'Novo Cupom'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Código do Cupom</Label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="DESCONTO10"
                required
                data-testid="coupon-code-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo de Desconto</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => setFormData({ ...formData, type: value })}
                >
                  <SelectTrigger data-testid="coupon-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Porcentagem (%)</SelectItem>
                    <SelectItem value="fixed">Valor Fixo (R$)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Valor</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                  required
                  data-testid="coupon-value-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Válido de</Label>
                <Input
                  type="datetime-local"
                  value={formData.valid_from}
                  onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                  required
                  data-testid="coupon-valid-from-input"
                />
              </div>
              <div>
                <Label>Válido até</Label>
                <Input
                  type="datetime-local"
                  value={formData.valid_until}
                  onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                  required
                  data-testid="coupon-valid-until-input"
                />
              </div>
            </div>

            <div>
              <Label>Máximo de Usos (Opcional)</Label>
              <Input
                type="number"
                value={formData.max_uses}
                onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                placeholder="Deixe em branco para ilimitado"
                data-testid="coupon-max-uses-input"
              />
            </div>

            <div className="flex space-x-2 pt-4">
              <Button type="submit" className="flex-1" data-testid="coupon-submit-btn">
                {editingCoupon ? 'Atualizar' : 'Criar'} Cupom
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                data-testid="coupon-cancel-btn"
              >
                Cancelar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};